import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Pin, Pencil, Trash2, ImagePlus, Loader2, X, Send, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, fileUrl } from "../lib/api";
import { Avatar } from "./common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const ago = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "agora";
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return new Date(iso).toLocaleDateString("pt-BR");
};

const MediaPicker = ({ media, setMedia, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...(e.target.files || [])].slice(0, 6 - media.length);
    setBusy(true);
    const added = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        added.push({ path: data.path, type: f.type.startsWith("video/") ? "video" : "image" });
      } catch (err) { toast.error(errMsg(err)); }
    }
    setMedia([...media, ...added]);
    setBusy(false);
    e.target.value = "";
  };
  return (
    <>
      {media.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {media.map((m, i) => (
            <div key={m.path} className="relative h-20 w-20 overflow-hidden rounded-lg bg-[#0B0E14]">
              {m.type === "video" ? <video src={fileUrl(m.path)} muted className="h-full w-full object-cover" /> : <img src={fileUrl(m.path)} alt="" className="h-full w-full object-cover" />}
              <button type="button" onClick={() => setMedia(media.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
      <button type="button" data-testid={testid} disabled={busy || media.length >= 6} onClick={() => ref.current?.click()} className="btn-ghost flex items-center gap-1.5 text-xs">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}Foto / vídeo
      </button>
      <input ref={ref} type="file" multiple accept="image/*,video/*" className="hidden" onChange={pick} />
    </>
  );
};

const Composer = ({ onPosted }) => {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const post = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/posts", { text, media });
      setText(""); setMedia([]); onPosted(data);
      toast.success("Publicado!");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-x p-4" data-testid="feed-composer">
      <div className="flex gap-3">
        <Avatar user={user} size={38} />
        <textarea data-testid="feed-text-input" rows={2} className="inp flex-1" placeholder="Compartilhe algo com o circuito..." value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <MediaPicker media={media} setMedia={setMedia} testid="feed-media-btn" />
      <div className="mt-3 flex justify-end">
        <button data-testid="feed-post-btn" disabled={busy || (!text.trim() && !media.length)} onClick={post} className="btn-lime flex items-center gap-1.5 rounded-full px-5 py-2 text-sm disabled:opacity-50"><Send className="h-4 w-4" />Publicar</button>
      </div>
    </div>
  );
};

const EditPost = ({ post, onClose, onSaved }) => {
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]);
  useEffect(() => { if (post) { setText(post.text); setMedia(post.media); } }, [post]);
  const save = async () => {
    try { const { data } = await api.put(`/posts/${post.id}`, { text, media }); onSaved(data); onClose(); toast.success("Postagem atualizada"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#232B3E] bg-[#121722] sm:max-w-lg">
        <DialogHeader><DialogTitle className="h-display text-3xl">Editar postagem</DialogTitle></DialogHeader>
        <textarea data-testid="edit-post-text" rows={4} className="inp" value={text} onChange={(e) => setText(e.target.value)} />
        <MediaPicker media={media} setMedia={setMedia} testid="edit-post-media-btn" />
        <button data-testid="edit-post-save" onClick={save} className="btn-lime mt-2 w-full rounded-full py-3">Salvar</button>
      </DialogContent>
    </Dialog>
  );
};

const PostCard = ({ p, onChange, onEdit }) => {
  const { user, isAdmin } = useAuth();
  const mine = p.author_id === user.user_id;
  const liked = (p.likes || []).includes(user.user_id);
  const act = async (fn) => { try { const { data } = await fn(); onChange({ ...p, ...data }); } catch (e) { toast.error(errMsg(e)); } };
  const del = async () => { if (!window.confirm("Excluir postagem?")) return; await api.delete(`/posts/${p.id}`); onChange(null, p.id); toast.success("Postagem excluída"); };
  const cols = p.media.length === 1 ? "grid-cols-1" : "grid-cols-2";
  return (
    <div data-testid={`post-${p.id}`} className={`card-x p-4 ${p.pinned ? "border-[#E4F222]/60" : ""}`}>
      {p.pinned && <div className="mb-2 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#E4F222]"><Pin className="h-3.5 w-3.5 fill-[#E4F222]" />Fixado</div>}
      <div className="flex items-center gap-3">
        <Link to={`/app/atletas/${p.author_id}`}><Avatar user={p.author} size={38} /></Link>
        <div className="min-w-0 flex-1">
          <Link to={`/app/atletas/${p.author_id}`} className="truncate font-semibold hover:text-[#E4F222]">{p.author?.name || "Atleta"}</Link>
          <div className="text-[11px] text-[#64748B]">{ago(p.created_at)}{p.updated_at && " · editado"}</div>
        </div>
        {isAdmin && <button data-testid={`pin-post-${p.id}`} title={p.pinned ? "Desafixar" : "Fixar"} onClick={() => act(() => api.post(`/posts/${p.id}/pin`))} className={`rounded-lg p-2 ${p.pinned ? "text-[#E4F222]" : "text-[#64748B] hover:text-white"}`}><Pin className="h-4 w-4" /></button>}
        {(mine || isAdmin) && <button data-testid={`edit-post-${p.id}`} onClick={() => onEdit(p)} className="rounded-lg p-2 text-[#64748B] hover:text-white"><Pencil className="h-4 w-4" /></button>}
        {(mine || isAdmin) && <button data-testid={`delete-post-${p.id}`} onClick={del} className="rounded-lg p-2 text-[#64748B] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>}
      </div>
      {p.text && <p className="mt-3 whitespace-pre-wrap text-sm text-[#e2e8f0]">{p.text}</p>}
      {p.media.length > 0 && (
        <div className={`mt-3 grid gap-1.5 overflow-hidden rounded-xl ${cols}`}>
          {p.media.map((m) => m.type === "video"
            ? <video key={m.path} src={fileUrl(m.path)} controls playsInline className="max-h-[480px] w-full bg-black object-contain" />
            : <img key={m.path} src={fileUrl(m.path)} alt="" className={`w-full object-cover ${p.media.length === 1 ? "max-h-[520px]" : "aspect-square"}`} />)}
        </div>
      )}
      <button data-testid={`like-post-${p.id}`} onClick={() => act(() => api.post(`/posts/${p.id}/like`))} className={`mt-3 flex items-center gap-1.5 text-sm ${liked ? "text-red-400" : "text-[#94A3B8] hover:text-white"}`}>
        <Heart className={`h-4 w-4 ${liked ? "fill-red-400" : ""}`} />{(p.likes || []).length}
      </button>
    </div>
  );
};

export const Feed = () => {
  const [posts, setPosts] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/posts").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);
  const change = (np, removedId) => {
    if (!np) return setPosts(posts.filter((x) => x.id !== removedId));
    if (np.pinned !== posts.find((x) => x.id === np.id)?.pinned) return load();
    setPosts(posts.map((x) => (x.id === np.id ? { ...x, ...np } : x)));
  };
  return (
    <div className="space-y-3" data-testid="home-feed">
      <div className="eyebrow flex items-center gap-1.5"><Newspaper className="h-3.5 w-3.5" />Feed do circuito</div>
      <Composer onPosted={() => load()} />
      {posts?.length === 0 && <p className="text-sm text-[#64748B]">Nenhuma publicação ainda. Seja o primeiro!</p>}
      {posts?.map((p) => <PostCard key={p.id} p={p} onChange={change} onEdit={setEdit} />)}
      <EditPost post={edit} onClose={() => setEdit(null)} onSaved={(np) => change(np)} />
    </div>
  );
};
